/* eslint-disable import/no-named-as-default */
"use client";

import { Vehicle, VehicleStatusType } from "@alba-cars/common-modules";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";

import CarLocationCard from "./CarLocationCard";
import WarrantyDownpaymentCard from "./inspection-report/WarrantyDownpaymentCard";
import TestDriveButton from "./TestDriveButton";
import PhoneNumberModal from "@/components/common/modals/PhoneNumberModal";
import IconDirham from "@/components/Icons/IconDirham";
import IconEye from "@/components/Icons/IconEye";
import Button from "@/components/ui/buttons/Button";
import { CarFavoriteButton } from "@/components/ui/buttons/CarFavoriteButton";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { initiateCallUs } from "@/lib/analytics/openai-events";
import { AgentVehicle } from "@/lib/types/vehicle-agent";
import { formatPriceWithDirham, getCarMonthlyInstallment, getCarPrice, slugify } from "@/lib/utils";
import { isReservedConsignment } from "@/lib/vehicle-ownership";
import { phoneNumber } from "@/utils/constants";
import { getCarNameTitle } from "@/utils/utils";

function resolvePrice(car: Vehicle) {
  const isTimedSaleActive = car.finance?.isTimedSaleActive === true && Number(car.finance?.salePrice) > 0;

  if (isTimedSaleActive) {
    const installment =
      Number(car.finance?.saleMonthlyInstallment) > 0
        ? Number(car.finance!.saleMonthlyInstallment)
        : getCarMonthlyInstallment(car);
    return { price: Number(car.finance!.salePrice), installment, isSalePrice: true };
  }

  const hasDroppedPrice = car.finance?.vehiclePriceType === "dropped_price" && Number(car.finance?.droppedPrice) > 0;

  if (hasDroppedPrice) {
    const installment =
      Number(car.finance?.droppedMonthlyInstallment) > 0
        ? Number(car.finance!.droppedMonthlyInstallment)
        : getCarMonthlyInstallment(car);
    return { price: Number(car.finance!.droppedPrice), installment, isSalePrice: false };
  }

  return {
    price: Number(car.finance?.price) || getCarPrice(car),
    installment: getCarMonthlyInstallment(car),
    isSalePrice: false,
  };
}

const CarSidebar = ({
  car,
  viewerCount,
  isMobileView,
  showLocationDetails,
}: {
  car: AgentVehicle;
  viewerCount: number;
  isMobileView: boolean;
  showLocationDetails: boolean;
}) => {
  const t = useTranslations("CarDetails.sidebar");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isPhoneNumberModalOpen, setIsPhoneNumberModalOpen] = useState(false);

  const trim = car?.trim ?? "";
  const refId = car?.refId ?? "";
  const make = car?.make?.name ?? "";
  const model = car?.model?.name ?? "";

  const { price, installment, isSalePrice } = resolvePrice(car);

  const isTabletOrLarger = useMediaQuery("(min-width: 768px)");

  const isSold = car.status === VehicleStatusType.SOLD;
  const isCarOfTheDay = isReservedConsignment(car);
  // const isOnSale = car.isOnSale === true;
  const buttonDisabled = isSold || isCarOfTheDay;

  return (
    <div className="flex flex-col gap-y-4">
      <div className="rounded-3xl bg-white">
        {/* {isOnSale && (
          <Announcement
            isVisible
            desktopImageSrc={ramadanBannerDesktop}
            mobileImageSrc={ramadanBannerMobile}
            className="!h-28 lg:h-auto"
            desktopImageClassName="h-full ![border-radius:1.5rem_1.5rem_0_0]"
            mobileImageClassName="h-full rounded-3xl"
          />
        )} */}
        <div className="px-7 pb-4 pt-6">
          <div className="flex items-center justify-between">
            {!isMobileView ? (
              <h1 className="max-w-xs text-wrap font-urbanist text-3xl font-semibold text-black">
                {getCarNameTitle(car, { showTrim: true })}
              </h1>
            ) : null}

            <CarFavoriteButton
              carId={car.id}
              className="transition-transform duration-700 hover:scale-125"
              withTooltip
            />
          </div>
          <div className="flex items-center">
            <p className="font-inter text-base font-medium text-black text-opacity-50">
              {t("stockNo")} {car.chassisNumber}
            </p>
          </div>

          <div className="mt-6 rounded-2xl">
            <div className="mb-2">
              <p className="mb-2 flex items-center gap-2 font-urbanist text-3xl font-bold">
                <span dir="ltr" className="flex items-center gap-1">
                  <IconDirham width="24" height="24" className="mb-1 self-center" />
                  {formatPriceWithDirham(price)}
                </span>
                {isSalePrice && (
                  <div className="relative inline-flex">
                    <span className="absolute inset-0 animate-pulse-sale rounded-md bg-primary opacity-10" />
                    <span className="relative inline-flex items-center rounded-md bg-primary-dark px-2 py-1 font-urbanist text-xs font-semibold text-white">
                      {t("salePrice")}
                    </span>
                  </div>
                )}
                {!isSalePrice && (
                  <span className="mb-1 self-end font-inter text-xs text-black/50">
                    {car?.finance?.vatInclusive === "vat_exclusive" ? t("vatExclusive") : t("vatInclusive")}
                  </span>
                )}
              </p>
              {isSalePrice && (
                <p className="mb-2 font-inter text-xs text-black/50">
                  {car?.finance?.vatInclusive === "vat_exclusive" ? t("vatExclusive") : t("vatInclusive")}
                </p>
              )}
            </div>
            {!!installment && (
              <span className="inter flex items-center gap-1 text-base font-bold text-success">
                <span dir="ltr" className="flex items-center gap-1">
                  <IconDirham width="16" height="16" fill="#22c55e" />
                  {formatPriceWithDirham(installment)}
                </span>
                {t("perMonth")}
              </span>
            )}
          </div>

          <p className="justify-strat mb-4 mt-6 flex items-center space-x-2 font-inter text-base font-medium lg:text-wrap xl:text-nowrap">
            <span className="flex items-start space-x-2 text-sm">
              <IconEye className="lg:mt-1 xl:my-auto" />
              <span>
                <span className="pr-1 font-bold text-primary">
                  {viewerCount} {t("people")}
                </span>{" "}
                {t("viewingNow")}
              </span>
            </span>
          </p>
          <div className="rounded-3xl bg-white">
            <TestDriveButton car={car} />
            <div className="flex gap-4">
              {" "}
              <Button
                buttonText={t("callUs")}
                bgColor="primary-light"
                textColor="primary"
                className="my-2 w-1/2 py-4 text-center transition-all duration-300 ease-in-out hover:bg-primary hover:text-white"
                onClick={() => {
                  initiateCallUs({
                    phone: phoneNumber,
                    isDesktop: isTabletOrLarger,
                    onOpenModal: () => setIsPhoneNumberModalOpen(true),
                    source: "car_sidebar_call_us",
                  });
                }}
              />
              <Button
                buttonText={t("buyThisCar")}
                bgColor="primary-light"
                disabled={buttonDisabled}
                textColor="primary"
                className="my-2 w-1/2 py-4 text-center transition-all duration-300 ease-in-out hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  if (buttonDisabled) {
                    return;
                  }

                  const params = new URLSearchParams(searchParams);
                  params.set("deposit", "5000");
                  const routeTo = trim
                    ? `/${locale}/book_car/${refId}-${slugify(make)}-${slugify(model)}-${slugify(trim)}`
                    : `/${locale}/book_car/${refId}-${slugify(make)}-${slugify(model)}`;
                  router.push(`${routeTo}?${params.toString()}`);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {!!installment && <WarrantyDownpaymentCard />}
      {showLocationDetails ? <CarLocationCard car={car} compact /> : null}

      <PhoneNumberModal
        isOpen={isPhoneNumberModalOpen}
        onClose={() => setIsPhoneNumberModalOpen(false)}
        phoneNumber={phoneNumber}
        trackingSource="car_sidebar_call_us"
      />
    </div>
  );
};

export default CarSidebar;
